<!--
  @component SettingsDrawer — project settings in a slide-over.
  tier:      screen-adjacent (opened from Overview; no tab jump)
  reads:     useDeploymentIO  → projects.get / setDeployImage / upsert (REAL)
             useIdentityIO    → owner
             useValidationIO  → input pre-check
  scope:     deployment settings — the one real control is the deploy
             image; env vars are editable (persist with the deploy once
             connected); team shows the real owner; project deletion is
             honestly pending the security layer. No fabricated secrets.
-->
<script lang="ts">
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useValidationIO } from "../../../../global/io/nodes/security/ValidationIO.svelte";
  import Card from "../../../../global/visuals/primitives/Card.svelte";
  import StatusNote from "../../../../global/visuals/primitives/StatusNote.svelte";
  import EnvSecretsPanel from "../../../../global/visuals/EnvSecretsPanel.svelte";

  interface Props {
    open: boolean;
    onClose: () => void;
  }
  let { open, onClose }: Props = $props();

  const deployment = useDeploymentIO();
  const identity = useIdentityIO();
  const inputValidation = useValidationIO();

  const project = $derived(identity.currentProject);

  // ── Deploy image (REAL, persisted server-side) ─────────────────
  interface ServerProject {
    readonly id: string;
    readonly name: string;
    readonly deployImage: string | null;
    readonly region: string;
  }
  let serverProject = $state<ServerProject | null>(null);
  let imageDraft = $state("");
  let imageSaving = $state(false);
  let imageSaved = $state(false);
  let imageError = $state<string | null>(null);
  const imageDirty = $derived(
    serverProject !== null && imageDraft.trim() !== (serverProject.deployImage ?? ""),
  );
  const imageCheck = $derived(inputValidation.check(imageDraft, "path"));

  $effect(() => {
    const id = project?.id;
    if (!id || !open) return;
    void (async () => {
      try {
        const fetched = (await deployment.dispatch("projects.get", { id })) as ServerProject | null;
        if (project?.id !== id) return;
        serverProject = fetched;
        imageDraft = fetched?.deployImage ?? "";
        imageError = null;
      } catch {
        serverProject = null;
        imageDraft = "";
      }
    })();
  });

  async function saveImage(): Promise<void> {
    if (!project || imageSaving) return;
    imageSaving = true;
    imageError = null;
    imageSaved = false;
    const next = imageDraft.trim();
    try {
      if (serverProject) {
        await deployment.dispatch("projects.setDeployImage", { id: project.id, image: next || null });
      } else {
        await deployment.dispatch("projects.upsert", { id: project.id, name: project.name, deployImage: next || null });
      }
      serverProject = { id: project.id, name: project.name, deployImage: next || null, region: serverProject?.region ?? "ewr" };
      imageSaved = true;
      setTimeout(() => { imageSaved = false; }, 2500);
    } catch (e) {
      imageError = e instanceof Error ? e.message : String(e);
    } finally {
      imageSaving = false;
    }
  }

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === "Escape") onClose();
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if open}
  <div class="scrim" onclick={onClose} role="presentation"></div>
  <div class="drawer" role="dialog" aria-modal="true" aria-label="Project settings">
    <header class="drawer-head">
      <div>
        <p class="eyebrow">Settings</p>
        <h2>{project?.name ?? "Project"}</h2>
      </div>
      <button type="button" class="close" onclick={onClose} aria-label="Close settings">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
    </header>

    <div class="drawer-body">
      <!-- Deploy image — the real control -->
      <Card eyebrow="Deployment" title="Deploy image">
        <p class="field-help">The container image your next deploy runs. Persisted for this project.</p>
        <div class="row">
          <input
            class="input mono"
            placeholder="registry.example.com/app:tag"
            bind:value={imageDraft}
            spellcheck="false"
          />
          <button
            type="button"
            class="btn-primary"
            disabled={!imageDirty || imageSaving || !imageCheck.ok}
            onclick={() => void saveImage()}
          >
            {imageSaving ? "Saving…" : imageSaved ? "Saved" : "Save"}
          </button>
        </div>
        {#if !imageCheck.ok && imageDraft}<p class="err">That image reference doesn't look valid.</p>{/if}
        {#if imageError}<p class="err">{imageError}</p>{/if}
      </Card>

      <!-- Environment & secrets — real, persisted, injected at deploy -->
      <Card eyebrow="Deployment" title="Environment & secrets">
        <p class="field-help">Env vars, encrypted secrets, and connected resources your app boots with — applied on every deploy.</p>
        {#if project}
          <EnvSecretsPanel projectId={project.id} />
        {/if}
      </Card>

      <!-- Team -->
      <Card eyebrow="Project" title="Team">
        {#if project && identity.user}
          <div class="member">
            <span class="avatar">{(identity.user.displayName ?? identity.user.email).charAt(0).toUpperCase()}</span>
            <div class="member-id">
              <p class="member-name">{identity.user.displayName ?? identity.user.email}</p>
              <p class="member-email">{identity.user.email}</p>
            </div>
            <span class="role-pill">Owner</span>
          </div>
        {/if}
        <StatusNote tone="soon" message="Inviting teammates arrives with the security layer." />
      </Card>

      <!-- Danger zone -->
      <Card eyebrow="Project" title="Delete project">
        <p class="field-help">Permanently removes this project and everything in it.</p>
        <StatusNote tone="pending" message="Project deletion connects with the security layer — coming soon." />
      </Card>
    </div>
  </div>
{/if}

<style>
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(2px);
    z-index: 200;
    animation: fade 0.18s ease-out;
  }
  .drawer {
    position: fixed;
    top: 0; right: 0; bottom: 0;
    width: min(480px, 92vw);
    background: var(--surface-base);
    border-left: 0.5px solid var(--border);
    box-shadow: var(--shadow-lg);
    z-index: 201;
    display: flex;
    flex-direction: column;
    font-family: var(--type-font);
    animation: slide 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  @keyframes fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slide { from { transform: translateX(100%); } to { transform: translateX(0); } }

  .drawer-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-5) var(--space-5) var(--space-4);
    border-bottom: 0.5px solid var(--border);
  }
  .eyebrow { margin: 0 0 2px; font-size: 10.5px; font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-tertiary); }
  .drawer-head h2 { margin: 0; font-size: 17px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .close {
    width: 32px; height: 32px;
    display: grid; place-items: center;
    background: transparent; border: 0; border-radius: var(--radius-md);
    color: var(--icon); cursor: pointer;
    transition: background var(--ease-fast), color var(--ease-fast);
  }
  .close:hover { background: var(--bg-hover); color: var(--icon-hover); }
  .close svg { width: 18px; height: 18px; }

  .drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    scrollbar-width: thin;
    scrollbar-color: var(--border) transparent;
  }

  .field-help { margin: 0 0 var(--space-3); font-size: 12.5px; color: var(--text-secondary); line-height: 1.5; }
  .row { display: flex; gap: var(--space-2); }
  .input {
    flex: 1;
    min-width: 0;
    padding: 9px var(--space-3);
    border-radius: var(--radius-md);
    border: 0.5px solid var(--border-input);
    background: var(--surface-sunken);
    color: var(--text-primary);
    font: inherit;
    font-size: 13px;
  }
  .input:focus { outline: none; border-color: var(--border-focus); }
  .input.mono { font-family: var(--type-mono); font-size: 12px; }
  .err { margin: var(--space-2) 0 0; font-size: 12px; color: var(--error); }

  .btn-primary {
    appearance: none;
    border-radius: var(--radius-md);
    padding: 0 var(--space-4);
    height: 38px;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: background var(--ease-fast), border-color var(--ease-fast);
  }
  .btn-primary { background: var(--bg-active); color: var(--text-primary); border: 0.5px solid var(--border-strong); }
  .btn-primary:hover:not(:disabled) { background: var(--bg-selected); border-color: var(--border-focus); }
  .btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }

  .member { display: flex; align-items: center; gap: var(--space-3); margin-bottom: var(--space-3); }
  .avatar {
    width: 32px; height: 32px;
    border-radius: var(--radius-pill);
    display: grid; place-items: center;
    font-size: 13px; font-weight: 600;
    color: rgba(255,255,255,0.78);
    background: linear-gradient(180deg, rgba(42,42,52,0.9), rgba(28,28,36,0.86));
    border: 0.5px solid rgba(255,255,255,0.1);
    flex-shrink: 0;
  }
  .member-id { flex: 1; min-width: 0; }
  .member-name { margin: 0; font-size: 13px; color: var(--text-primary); }
  .member-email { margin: 0; font-size: 12px; color: var(--text-tertiary); }
  .role-pill { font-size: 11px; color: var(--text-secondary); background: var(--bg-hover); border: 0.5px solid var(--border-subtle); border-radius: var(--radius-pill); padding: 2px var(--space-2); }
</style>
